import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareLevels = (l1, l2) => l1.internalid === l2.internalid;

const levelSlice = createSlice({
    name: 'levels',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const l of ensureArray(action.payload)) {
                let idx = state.data.findIndex(l2 => compareLevels(l, l2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, l);
                } else {
                    state.data.push(l);
                }
            }
        },
        remove: (state, action) => {
            for (const l of ensureArray(action.payload)) {
                let idx = state.data.findIndex(s => compareLevels(s, l));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => levelSlice.getInitialState());
    }
});

// #region network operations
const fetchLevels = network => network.get('/api/v1/levels', levelSlice.actions.set);
export const createLevel = (network, level) => network.post('/api/v1/level', level, levelSlice.actions.addOrUpdate);
export const updateLevel = (network, level) => network.put('/api/v1/level', level, levelSlice.actions.addOrUpdate);
export const removeLevel = (network, level) => network.delete('/api/v1/level/' + encodeURIComponent(level.internalid), levelSlice.actions.remove);
// #endregion

// #region permission checks
export const canUpdateLevel = (credentials, site) => credentials.access.hasFull('administrator');
export const canCreateLevel = (credentials) => credentials.access.hasFull('administrator');
export const canRemoveLevel = (credentials, site) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectLevels = state => state.levels.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectLevelByName = createSelector([selectLevels, (levels, site) => site, (levels, site, name) => name], (levels, site, name) => {
    let result = levels.find(l => (!site || l.site === site) && l.name === name);
    if (!result && site) {
        result = levels.find(l => !l.site && l.name === name);
    }
    return result;
});
const selectLevelBySite = createSelector([selectLevels, (levels, name) => name], (levels, site) => levels.filter(l => site == null || l.site === null || l.site === site));
const selectLevel = (site, name) => state => selectLevelByName(state, site, name);
const selectSiteLevels = site => state => selectLevelBySite(state, site);
// #endregion

// #region listeners
const listen = {
    levelCreated: (level, { dispatch }) => dispatch(levelSlice.actions.addOrUpdate(level)),
    levelUpdated: (level, { dispatch }) => dispatch(levelSlice.actions.addOrUpdate(level)),
    levelRemoved: (level, { dispatch }) => dispatch(levelSlice.actions.remove(level))
}
// #endregion
const initLevels = { run: fetchLevels, listen, authenticated: false, immortal: true }
export const [useLevels, realizeLevels] = dataHookFactory(selectLevels, initLevels);
export const [useLevel, realizeLevel] = dataHookFactory(selectLevel, name => initLevels);
export const [useSiteLevels, realizeSiteLevels] = dataHookFactory(selectSiteLevels, site => initLevels);

const reducerDetails = { name: levelSlice.name, reducer: levelSlice.reducer };
export default reducerDetails;